import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Grid,
    Box,
    Alert
} from '@mui/material';
import api from '../../utils/api';

const UserDialogs = ({
    open,
    onClose,
    type,
    selectedUser,
    branches,
    selectedBranch,
    onAssignUser,
    onRemoveUser,
    onUpdateRole,
    onTransferUser
}) => {
    const [formData, setFormData] = useState({
        userId: '',
        email: '',
        isBranchAdmin: false,
        targetBranchId: ''
    });
    const [error] = useState('');

    const handleChange = (event) => {
        const { name, value, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'isBranchAdmin' ? checked : value
        }));
    };

    const handleSubmit = () => {
        switch (type) {
            case 'assign':
                onAssignUser(formData.userId, formData.isBranchAdmin);
                break;
            case 'edit':
                onUpdateRole(selectedUser.id, formData.isBranchAdmin);
                break;
            case 'transfer':
                onTransferUser(selectedUser.id, formData.targetBranchId);
                break;
            case 'remove':
                onRemoveUser(selectedUser.id);
                break;
            default:
                break;
        }
        onClose();
    };

    const renderDialogContent = () => {
        switch (type) {
            case 'assign':
                return (
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="User Email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isBranchAdmin}
                                        onChange={handleChange}
                                        name="isBranchAdmin"
                                    />
                                }
                                label="Branch Admin"
                            />
                        </Grid>
                    </Grid>
                );

            case 'edit':
                return (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isBranchAdmin}
                                onChange={handleChange}
                                name="isBranchAdmin"
                            />
                        }
                        label="Branch Admin"
                    />
                );

            case 'transfer':
                return (
                    <FormControl fullWidth>
                        <InputLabel>Target Branch</InputLabel>
                        <Select
                            value={formData.targetBranchId}
                            label="Target Branch"
                            name="targetBranchId"
                            onChange={handleChange}
                            sx={{ minHeight: 56 }}
                        >
                            {branches
                                .filter(branch => branch.id !== selectedBranch)
                                .map(branch => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                );

            case 'remove':
                return (
                    <p>
                        Are you sure you want to remove {selectedUser?.first_name}{' '}
                        {selectedUser?.last_name} from this branch?
                    </p>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {type === 'assign' && 'Assign New User'}
                {type === 'edit' && 'Edit User Role'}
                {type === 'transfer' && 'Transfer User'}
                {type === 'remove' && 'Remove User'}
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {renderDialogContent()}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color={type === 'remove' ? 'error' : 'primary'}
                    onClick={handleSubmit}
                >
                    {type === 'remove' ? 'Remove' : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const AddUserDialog = ({ open, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        role: 'cashier',
        country: 'Kenya'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await api.post('/auth/users', formData);
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error creating user:', error);
            setError(error.response?.data?.message || 'Error creating user');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add New User</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                required
                                fullWidth
                                label="First Name"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                required
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                required
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                placeholder="+254700000000"
                            />
                        </Grid>
                        <Grid size={12}>
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    label="Role"
                                    sx={{ minHeight: 56 }}
                                >
                                    <MenuItem value="cashier">Cashier</MenuItem>
                                    <MenuItem value="viewer">Viewer</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Adding...' : 'Add User'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const EditUserDialog = ({ open, onClose, user, onSuccess }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        role: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                phoneNumber: user.phoneNumber || '',
                role: user.role || ''
            });
        }
    }, [user]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await api.put(`/auth/users/${user.id}`, formData);
            onSuccess?.(response.data.user);
            onClose();
        } catch (error) {
            console.error('Error updating user:', error);
            setError(error.response?.data?.message || 'Error updating user');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit User</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                required
                                fullWidth
                                label="First Name"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                required
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                required
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={12}>
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select
                                    name="role"
                                    value={formData.role}
                                    label="Role"
                                    onChange={handleChange}
                                    sx={{ minHeight: 56 }}
                                >
                                    {formData.role === 'admin' && <MenuItem value="admin">Admin</MenuItem>}
                                    <MenuItem value="user">User</MenuItem>
                                    <MenuItem value="manager">Manager</MenuItem>
                                    <MenuItem value="cashier">Cashier</MenuItem>
                                    <MenuItem value="viewer">Viewer</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={handleSubmit}
                >
                    {loading ? 'Updating...' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const DeleteUserDialog = ({ open, onClose, user }) => {
    const [error, setError] = useState('');

    const handleDelete = async () => {
        try {
            await api.delete(`/users/${user.id}`);
            onClose();
        } catch (error) {
            setError(error.response?.data?.message || 'Error deleting user');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Delete User</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <p>Are you sure you want to delete {user.name}? This action cannot be undone.</p>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleDelete} variant="contained" color="error">
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserDialogs; 